import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import { debounce } from 'lodash';

const MapSyncContext = createContext();

export const MapSyncProvider = ({ children }) => {
  const [mapState, setMapState] = useState({
    center: [58.505, .09],  // Valores iniciales
    zoom: 1,
  });

  const mapsRef = useRef([]);
  const syncingRef = useRef(false);

  const updateMapState = useCallback((newState) => {
    setMapState((prevState) => ({
      ...prevState,
      ...newState,
    }));
  }, []);

  const syncMaps = useCallback((syncState) => {
    setMapState(syncState);
  }, []);

  // Debounced update strategy for React state to not lag the UI
  const debouncedUpdateState = useRef(
    debounce((center, zoom) => {
      setMapState((prev) => {
        if (prev.zoom === zoom && prev.center[0] === center[0] && prev.center[1] === center[1]) return prev;
        return { center, zoom };
      });
    }, 500)
  ).current;

  // Immediate map instance visual sync!
  const onMapMove = useCallback((e) => {
    if (syncingRef.current) return;
    
    syncingRef.current = true;
    const sourceMap = e.target;
    const centerObj = sourceMap.getCenter();
    const zoom = sourceMap.getZoom();
    const bearing = sourceMap.getBearing();
    const pitch = sourceMap.getPitch();

    mapsRef.current.forEach((targetMap) => {
      if (targetMap !== sourceMap) {
        targetMap.jumpTo({
          center: centerObj,
          zoom: zoom,
          bearing: bearing,
          pitch: pitch
        });
      }
    });

    // Also update context state purely for persistence when unmounting/remounting
    debouncedUpdateState([centerObj.lat, centerObj.lng], zoom);

    syncingRef.current = false;
  }, [debouncedUpdateState]);

  const internalRegisterMap = useCallback((map) => {
    if (!mapsRef.current.includes(map)) {
      mapsRef.current.push(map);
      map.on('move', onMapMove);
    }
  }, [onMapMove]);

  const internalUnregisterMap = useCallback((map) => {
    mapsRef.current = mapsRef.current.filter((m) => m !== map);
    map.off('move', onMapMove);
  }, [onMapMove]);

  return (
    <MapSyncContext.Provider value={{ 
        mapState, 
        updateMapState, 
        syncMaps,
        internalRegisterMap,
        internalUnregisterMap
    }}>
      {children}
    </MapSyncContext.Provider>
  );
};

export const useMapSync = () => {
  const context = useContext(MapSyncContext);
  if (!context) {
    throw new Error('useMapSync must be used within a MapSyncProvider');
  }
  return context;
};
